/** @jsx jsx */
import { Box, Button, Container, Flex, Loader } from "@eworkit/iris";
import { Form, Formik } from "formik";
import { FC, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { jsx } from "theme-ui";

import { UserContext } from "@/components/User";
import { Attachment, cloneFile } from "@/data/attachments";
import { fetchConsultantProfile, IConsultantProfile } from "@/data/consultant-profiles";
import { JobApplicationStep } from "@/data/job-applications/types/JobApplicationStep";
import { mapJobApplicationStepKey } from "@/data/job-applications/utils/mapJobApplicationStepKey";
import { maxJobApplicationStepKey } from "@/data/job-applications/utils/maxJobApplicationStepKey";

import { ApplyWizardBackButton } from "../_components/ApplyWizardBackButton";
import { useApplyWizard } from "../_components/ApplyWizardContext";
import {
  ApplyFilesFormFields,
  ApplyFilesFormValues,
  APPLY_FILES_DEFAULT_VALUES,
  APPLY_FILES_VALIDATION_SCHEMA,
  transformDraftWithFiles,
} from "./ApplyFilesFormFields";

interface ApplyWizardFilesProps {}

export const ApplyWizardFiles: FC<ApplyWizardFilesProps> = ({}) => {
  const { t } = useTranslation(["session", "common"]);
  const { contextCompany } = useContext(UserContext);

  const { draft, profile, setDraft, createOrUpdateDraft, goToNextStep } = useApplyWizard();

  const isInitialRender = useRef(true);

  const [initialCvs, setInitialCvs] = useState<Attachment[]>();
  const [originalProfile, setOriginalProfile] = useState<IConsultantProfile>();

  const handleSubmit = useCallback(
    async (values: ApplyFilesFormValues) => {
      try {
        await createOrUpdateDraft(draft => ({
          ...transformDraftWithFiles(values)(draft),
          step: maxJobApplicationStepKey(draft?.step, "FILES"),
        }));
      } catch (error) {
        // NOTE: errors are ignored on purpose
      } finally {
        goToNextStep();
      }
    },
    [createOrUpdateDraft, goToNextStep],
  );

  useEffect(() => {
    const initData = async () => {
      let profileWithCvs = profile;

      try {
        const originalProfile = await fetchConsultantProfile({
          consultantProfileId: profile!.parent ? profile!.parent.id : profile!.id!,
          companyId: contextCompany?.id,
        });

        setOriginalProfile(originalProfile);
        profileWithCvs = originalProfile;
      } catch (error) {
        // NOTE: errors are ignored on purpose
      }

      try {
        if (draft?.consultantProfile.cvs?.length) {
          setInitialCvs(draft.consultantProfile.cvs);
        } else if (draft?.step && mapJobApplicationStepKey[draft.step] >= JobApplicationStep.FILES) {
          setInitialCvs([]);
        } else {
          const profileCv = profileWithCvs?.cvs.find(x => x.defaultFile === true);

          if (profileCv) {
            const clonedProfileCv = await cloneFile({ systemId: profileCv.systemId });
            setInitialCvs([clonedProfileCv]);
          } else {
            setInitialCvs([]);
          }
        }

        setDraft(d => ({ ...d!, step: maxJobApplicationStepKey(draft?.step, "FILES") }));
      } catch (error) {
        setInitialCvs([]);
      }
    };

    if (isInitialRender.current) {
      initData();
      isInitialRender.current = false;
    }
  });

  if (!initialCvs) {
    return (
      <Container>
        <Flex alignItems="center" justifyContent="center" my="xl">
          <Loader size="xl" color="vg500" />
        </Flex>
      </Container>
    );
  }

  return (
    <Box py="md">
      <Container variant="narrow">
        <Formik<ApplyFilesFormValues>
          initialValues={{
            ...APPLY_FILES_DEFAULT_VALUES,
            cvs: initialCvs.map(cv => ({ attachment: cv })),
            attachments: draft?.attachments?.map(attachment => ({ attachment })) ?? [],
          }}
          validationSchema={APPLY_FILES_VALIDATION_SCHEMA}
          onSubmit={handleSubmit}>
          {formik => (
            <Form>
              <Flex px="sm" flexDirection="column" gap="sm">
                <ApplyWizardBackButton />

                <Flex flexDirection="column" gap="lg">
                  <ApplyFilesFormFields formik={formik} originalProfile={originalProfile} />

                  <Box>
                    <Button size="lg" onClick={formik.submitForm} disabled={formik.isSubmitting}>
                      {t("JOB_REQUESTS.APPLY.WIZARD.CONTINUE")}
                    </Button>
                  </Box>
                </Flex>
              </Flex>
            </Form>
          )}
        </Formik>
      </Container>
    </Box>
  );
};
